import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useI18n } from '../context/I18nContext';
import { API } from '../config';


export default function SuccessPage() {
  const { lang } = useI18n();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const pollStatus = async (attempt) => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (attempt < 5) {
          setTimeout(() => {
            setAttempts(attempt + 1);
            pollStatus(attempt + 1);
          }, 2000);
        } else {
          setStatus('pending');
        }
      } catch {
        setStatus('error');
      }
    };

    pollStatus(0);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="success-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative text-center max-w-md"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2" data-testid="success-loading">
              {lang === 'fr' ? 'Verification du paiement...' : 'Verifying payment...'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {lang === 'fr' ? 'Tentative' : 'Attempt'} {attempts + 1}/5
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground mb-3" data-testid="success-confirmed">
              {lang === 'fr' ? 'Paiement confirme !' : 'Payment confirmed!'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {lang === 'fr'
                ? 'Votre licence HexoPDF est active. Vous pouvez telecharger le logiciel depuis votre compte.'
                : 'Your HexoPDF license is active. You can download the software from your account.'}
            </p>
            <div className="flex flex-col gap-3">
              <Button asChild className="bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 shadow-[0_0_15px_rgba(59,130,246,0.4)]">
                <Link to="/account" data-testid="success-account-link">
                  <Download className="w-4 h-4 mr-2" />
                  {lang === 'fr' ? 'Aller a mon compte' : 'Go to my account'}
                </Link>
              </Button>
              <Button variant="ghost" asChild className="text-muted-foreground hover:text-white">
                <Link to="/" data-testid="success-home-link">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {lang === 'fr' ? 'Retour a l\'accueil' : 'Back to home'}
                </Link>
              </Button>
            </div>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground mb-3" data-testid="success-error">
              {status === 'expired'
                ? (lang === 'fr' ? 'Session expiree' : 'Session expired')
                : (lang === 'fr' ? 'Erreur de paiement' : 'Payment error')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {lang === 'fr' ? 'Veuillez reessayer.' : 'Please try again.'}
            </p>
            <Button variant="ghost" asChild className="text-muted-foreground hover:text-white">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {lang === 'fr' ? 'Retour' : 'Go back'}
              </Link>
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-foreground mb-3" data-testid="success-pending">
              {lang === 'fr' ? 'Paiement en cours de traitement...' : 'Payment processing...'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {lang === 'fr' ? 'Verifiez votre email pour confirmation.' : 'Check your email for confirmation.'}
            </p>
            <Button variant="ghost" asChild className="text-muted-foreground hover:text-white">
              <Link to="/account">
                {lang === 'fr' ? 'Aller a mon compte' : 'Go to my account'}
              </Link>
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
